import React, { useState, useEffect } from "react";
import { PanelWithHeader } from "../panels/PanelWithHeader";
import { PanelWithBreadCrumbAndSearchBox } from "../panels/PanelWithBreadCrumbAndSearchBox";
import {
  apiAddOrUpdateToDo,
  apiDeleteToDo,
  apiGetMyToDo,
  apiQuickUpdateToDo,
} from "../../services/ToDoService";
import { ToDoAddOrUpdatePopup } from "../todo/ToDoAddOrUpdatePopup";
import "../todo/ToDoListPage.css";

type ToDoListPageProps = {
  value: string;
};

let config = require("../../configuration/config");

export const ToDoListPage = (props: ToDoListPageProps) => {
  const [data, setData] = useState({ rows: [{}] });
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const [addOrUpdatePopupInformation, setAddOrUpdatePopupInformation] =
    useState<{ visible: boolean; item: any }>({ visible: false, item: undefined });

  console.log(config.serverUrl);

  useEffect(() => {
    ensureLatestData();
  }, []);

  let handlePopupClose = () => {
    setAddOrUpdatePopupInformation({ visible: false, item: undefined });
  };

  let handleAdd = () => {
    setAddOrUpdatePopupInformation({ visible: true, item: undefined });
  };

  let handleEdit = (item: any) => {
    setAddOrUpdatePopupInformation({ visible: true, item });
  };

  let handleSave = (formData: any) => {
    apiAddOrUpdateToDo(formData).then((response) => {
      handlePopupClose();
      ensureLatestData();
    });
  };

  let handleQuickUpdate = (id: any, completed: any) => {
    apiQuickUpdateToDo({ id: id, completed: completed }).then((response) => {
      ensureLatestData();
    });
  };

  let handleDelete = (id: any) => {
    if (!window.confirm("Opravdu úplně odebrat tento úkol?")) {
      return;
    }
    apiDeleteToDo(id).then(() => {
      ensureLatestData();
    });
  };

  let handlePostpone = (row: any, days: number) => {
    const base = row.dueDate ? new Date(row.dueDate) : new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + days);
    const year = base.getFullYear();
    const month = `${base.getMonth() + 1}`.padStart(2, "0");
    const day = `${base.getDate()}`.padStart(2, "0");
    const payload = {
      todoId: row.todoId,
      name: row.name,
      description: row.description,
      category: row.category,
      isRepeatable: row.isRepeatable,
      frequencyInDays: row.frequencyInDays,
      dueDate: `${year}-${month}-${day}`,
    };
    apiAddOrUpdateToDo(payload).then(() => {
      ensureLatestData();
    });
  };

  let ensureLatestData = () => {
    apiGetMyToDo().then((response) => {
      setData({ ...data, rows: response.data });
    });
  };

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const isOverdue = (row: any) =>
    row.dueDate && !row.completed && new Date(row.dueDate) < startOfToday;

  const isDue = (row: any) =>
    row.isRepeatable &&
    !row.completed &&
    (!row.dueDate || new Date(row.dueDate) <= endOfToday);

  const isUpcoming = (row: any) =>
    row.isRepeatable &&
    !row.completed &&
    row.dueDate &&
    new Date(row.dueDate) > endOfToday;

  const rows = (data.rows || []).filter((row: any) => row && row.todoId);

  const filteredRows = rows
    .filter((row: any) => {
      if (isUpcoming(row)) return false; // upcoming repeatable shown separately
      if (statusFilter === "active") return !row.completed;
      if (statusFilter === "inactive") return row.completed;
      return true;
    })
    .sort((a: any, b: any) => {
      const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return aTime - bTime;
    });

  const dueRepeatable = rows.filter((row: any) => isDue(row));

  const upcomingRepeatable = rows
    .filter((row: any) => isUpcoming(row))
    .sort(
      (a: any, b: any) =>
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );

  const gridRows = rows.filter((row: any) => !isUpcoming(row));
  const countAll = gridRows.length;
  const countActive = gridRows.filter((row: any) => !row.completed).length;
  const countInactive = gridRows.filter((row: any) => row.completed).length;

  let formatDate = (value: any) => {
    if (!value) return "";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("cs-CZ");
  };

  let daysToDue = (value: any) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return Math.round(
      (date.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  let daysToDueLabel = (value: any) => {
    const diff = daysToDue(value);
    if (diff === null) return "";
    if (diff === 0) return "dnes";
    if (diff === 1) return "zítra";
    if (diff === -1) return "1 den po termínu";
    if (diff < 0) return `${-diff} dní po termínu`;
    return `za ${diff} dní`;
  };

  return (
    <div className="container-fluid mt-3">
      {addOrUpdatePopupInformation.visible && (
        <ToDoAddOrUpdatePopup
          handleClose={handlePopupClose}
          handleConfirmation={handleSave}
          item={addOrUpdatePopupInformation.item}
        />
      )}
      <PanelWithBreadCrumbAndSearchBox currentPageName="To Do" />

      {dueRepeatable.length > 0 && (
        <div className="row wow fadeIn">
          <div className="col-12 mb-4">
            <div className="todoWarnings">
              <div className="todoWarnings__title">
                <i className="fas fa-exclamation-triangle"></i>
                Je třeba udělat ({dueRepeatable.length})
              </div>
              <div className="todoWarnings__list">
                {dueRepeatable.map((row: any) => {
                  const overdue =
                    row.dueDate && new Date(row.dueDate) < endOfToday;
                  return (
                    <div key={row.todoId} className="todoWarnItem">
                      <div className="todoWarnItem__icon">
                        <i className="fas fa-sync-alt"></i>
                      </div>
                      <div className="todoWarnItem__body">
                        <div className="todoWarnItem__name">{row.name}</div>
                        <div
                          className={
                            "todoWarnItem__meta" + (overdue ? " overdue" : "")
                          }
                        >
                          {row.dueDate
                            ? (overdue ? "Prošlý termín: " : "Termín: ") +
                              formatDate(row.dueDate)
                            : "Bez termínu"}
                          {row.frequencyInDays
                            ? ` · každých ${row.frequencyInDays} dní`
                            : ""}
                        </div>
                      </div>
                      <button
                        className="todoWarnItem__done"
                        onClick={() => handleQuickUpdate(row.todoId, true)}
                      >
                        <i className="fas fa-check"></i> Hotovo
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row wow fadeIn">
        <div className="col-12 mb-4">
          <PanelWithHeader header="Úkoly" iconClassName="fas fa-tasks">
            <div className="todoToolbar">
              <div className="todoFilter">
                <button
                  className={
                    "todoChip" + (statusFilter === "all" ? " active" : "")
                  }
                  onClick={() => setStatusFilter("all")}
                >
                  Všechny ({countAll})
                </button>
                <button
                  className={
                    "todoChip" + (statusFilter === "active" ? " active" : "")
                  }
                  onClick={() => setStatusFilter("active")}
                >
                  Aktivní ({countActive})
                </button>
                <button
                  className={
                    "todoChip" + (statusFilter === "inactive" ? " active" : "")
                  }
                  onClick={() => setStatusFilter("inactive")}
                >
                  Neaktivní ({countInactive})
                </button>
              </div>
              <button className="todoAddBtn" type="button" onClick={handleAdd}>
                <i className="fas fa-plus"></i> Přidat nový
              </button>
            </div>

            <div className="table-responsive">
              <table className="todoTable">
                <thead>
                  <tr>
                    <th className="todoColSm">Stav</th>
                    <th>Název</th>
                    <th>Kategorie</th>
                    <th className="todoColSm">Termín</th>
                    <th>Opakování</th>
                    <th className="text-right">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="todoTable__empty">
                        <i className="fas fa-clipboard-check"></i>
                        <div>Žádné úkoly</div>
                      </td>
                    </tr>
                  )}
                  {filteredRows.map((row: any) => (
                    <tr
                      key={row.todoId}
                      className={row.completed ? "isCompleted" : ""}
                    >
                      <td className="todoColSm">
                        <label className="todoCheck">
                          <input
                            type="checkbox"
                            checked={!!row.completed}
                            onChange={(event) =>
                              handleQuickUpdate(row.todoId, event.target.checked)
                            }
                          />
                          <span className="mark"></span>
                        </label>
                      </td>
                      <td>
                        <div className="todoNameCell">
                          <span className="todoName">{row.name}</span>
                          {row.description && (
                            <span className="todoDesc" title={row.description}>
                              {row.description}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {row.category && (
                          <span className="todoCategoryChip">
                            {row.category}
                          </span>
                        )}
                      </td>
                      <td className="todoColSm">
                        {row.dueDate ? (
                          <div className="todoDueCell">
                            <span
                              className={
                                "todoDue" + (isOverdue(row) ? " overdue" : "")
                              }
                            >
                              {isOverdue(row) && (
                                <i className="fas fa-exclamation-circle"></i>
                              )}
                              {formatDate(row.dueDate)}
                            </span>
                            {!row.completed && (
                              <span
                                className={
                                  "todoDueDays" +
                                  (isOverdue(row) ? " overdue" : "")
                                }
                              >
                                {daysToDueLabel(row.dueDate)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#cfd5e1" }}>—</span>
                        )}
                      </td>
                      <td>
                        {row.isRepeatable ? (
                          <span
                            className={
                              "todoRepeatBadge" + (isDue(row) ? " overdue" : "")
                            }
                          >
                            <i className="fas fa-sync-alt"></i>
                            {row.dueDate ? formatDate(row.dueDate) : "opakuje se"}
                            {row.frequencyInDays
                              ? ` · ${row.frequencyInDays}d`
                              : ""}
                          </span>
                        ) : (
                          <span style={{ color: "#cfd5e1" }}>—</span>
                        )}
                      </td>
                      <td className="text-right">
                        {isOverdue(row) && (
                          <>
                            <button
                              className="todoAction postpone"
                              title="Posunout o den"
                              onClick={() => handlePostpone(row, 1)}
                            >
                              <i className="fas fa-angle-right"></i>
                            </button>
                            <button
                              className="todoAction postpone"
                              title="Posunout o týden"
                              onClick={() => handlePostpone(row, 7)}
                            >
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </>
                        )}
                        <button
                          className="todoAction edit"
                          title="Upravit"
                          onClick={() => handleEdit(row)}
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="todoAction delete"
                          title="Odebrat"
                          onClick={() => handleDelete(row.todoId)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {upcomingRepeatable.length > 0 && (
              <div className="todoUpcoming">
                <div className="todoUpcoming__title">
                  <i className="fas fa-calendar-alt"></i> Nadcházející úkoly
                </div>
                <div className="todoUpcoming__list">
                  {upcomingRepeatable.map((row: any) => (
                    <div key={"up_" + row.todoId} className="todoUpcoming__item">
                      <div className="todoUpcoming__date">
                        <i className="far fa-clock"></i>
                        {formatDate(row.dueDate)}
                        <span className="todoUpcoming__days">
                          {daysToDueLabel(row.dueDate)}
                        </span>
                      </div>
                      <div className="todoUpcoming__info">
                        <span className="todoUpcoming__name">{row.name}</span>
                        {row.frequencyInDays ? (
                          <span className="todoUpcoming__repeat">
                            <i className="fas fa-sync-alt"></i> každých{" "}
                            {row.frequencyInDays} dní
                          </span>
                        ) : null}
                      </div>
                      <div className="todoUpcoming__actions">
                        <button
                          className="todoAction done"
                          title="Označit jako hotové"
                          onClick={() => handleQuickUpdate(row.todoId, true)}
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          className="todoAction edit"
                          title="Upravit"
                          onClick={() => handleEdit(row)}
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="todoAction delete"
                          title="Odebrat"
                          onClick={() => handleDelete(row.todoId)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </PanelWithHeader>
        </div>
      </div>
    </div>
  );
};
